import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { campaignAPI } from '../../services/api';
import { toast } from 'react-toastify';
import DownloadProgress from '../Common/DownloadProgress';
import {
    DocumentTextIcon,
    PlusIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    PhoneIcon,
    ChatBubbleLeftRightIcon,
    SpeakerWaveIcon,
    EyeIcon,
    ArrowDownTrayIcon,
    PencilSquareIcon,
    TrashIcon,
    Cog6ToothIcon,
    XMarkIcon,
    ChevronUpIcon,
    ChevronDownIcon,
    EyeSlashIcon,
    CheckIcon,
    PhotoIcon,
    VideoCameraIcon,
    MusicalNoteIcon,
    DocumentIcon,
    EllipsisVerticalIcon,
    DocumentArrowDownIcon
} from '@heroicons/react/24/outline';

const Campaigns = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [paginating, setPaginating] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        type: '',
        page: 1,
        limit: 10,
    });
    const [pagination, setPagination] = useState({});
    const [showSettings, setShowSettings] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [campaignToDelete, setCampaignToDelete] = useState(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [downloading, setDownloading] = useState(null);
    const [downloadingPhoneNumbers, setDownloadingPhoneNumbers] = useState(null);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [downloadSpeed, setDownloadSpeed] = useState(0);
    const [downloadTotalRecords, setDownloadTotalRecords] = useState(0);
    const [downloadProcessedRecords, setDownloadProcessedRecords] = useState(0);
    const [downloadFileName, setDownloadFileName] = useState('');
    const [downloadError, setDownloadError] = useState(null);
    const [downloadStartTime, setDownloadStartTime] = useState(null);
    const [showDownloadProgress, setShowDownloadProgress] = useState(false);
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [showMediaModal, setShowMediaModal] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState('');
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const { isAdmin, token } = useAuth();

    // Helper function to create authenticated media URLs
    const getMediaUrl = (filename) => {
        if (!filename || !token) return '';
        const baseUrl = process.env.REACT_APP_MEDIA_CDN_URL || process.env.REACT_APP_MEDIA_URL || '/api/campaigns/media';
        return `${baseUrl}/${filename}?token=${encodeURIComponent(token)}`;
    };

    // All available columns with their configurations
    const allColumns = [
        { key: 'userName', label: 'User Name', visible: true, admin: true },
        { key: 'campaignType', label: 'Campaign Type', visible: true, admin: false },
        { key: 'campaignName', label: 'Campaign Name', visible: true, admin: false },
        { key: 'message', label: 'Message', visible: true, admin: false },
        { key: 'mediaFiles', label: 'Media Files', visible: true, admin: false },
        { key: 'dateCreated', label: 'Date Created', visible: true, admin: false },
        { key: 'totalNumbers', label: 'Total Numbers', visible: true, admin: false },
        { key: 'chargeCredit', label: 'Charge Credit', visible: false, admin: false },
        { key: 'totalCreditDeducted', label: 'Total Credit Deducted', visible: false, admin: false },
        { key: 'sent', label: 'Sent', visible: false, admin: false },
        { key: 'delivered', label: 'Delivered', visible: false, admin: false },
        { key: 'failed', label: 'Failed', visible: false, admin: false },
        { key: 'status', label: 'Status', visible: true, admin: false }
    ];

    // State for column management
    const [columnSettings, setColumnSettings] = useState(() => {
        const saved = localStorage.getItem('campaignColumnSettings');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                // Check if new columns exist in saved settings
                const hasNewColumns = parsed.some(col => col.key === 'message' || col.key === 'mediaFiles');
                if (!hasNewColumns) {
                    // Migrate old settings by adding new columns
                    const updatedColumns = [...allColumns];
                    localStorage.setItem('campaignColumnSettings', JSON.stringify(updatedColumns));
                    return updatedColumns;
                }
                return parsed;
            } catch {
                return allColumns;
            }
        }
        return allColumns;
    });

    // Get visible columns based on user role
    const getVisibleColumns = () => {
        return columnSettings.filter(col => {
            if (col.admin && !isAdmin()) return false;
            return col.visible;
        });
    };

    // Save column settings to localStorage
    const saveColumnSettings = (newSettings) => {
        setColumnSettings(newSettings);
        localStorage.setItem('campaignColumnSettings', JSON.stringify(newSettings));
    };

    // Toggle column visibility
    const toggleColumnVisibility = (columnKey) => {
        const newSettings = columnSettings.map(col =>
            col.key === columnKey ? { ...col, visible: !col.visible } : col
        );
        saveColumnSettings(newSettings);
    };

    // Move column up/down
    const moveColumn = (columnKey, direction) => {
        const currentIndex = columnSettings.findIndex(col => col.key === columnKey);
        if (currentIndex === -1) return;

        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (newIndex < 0 || newIndex >= columnSettings.length) return;

        const newSettings = [...columnSettings];
        [newSettings[currentIndex], newSettings[newIndex]] = [newSettings[newIndex], newSettings[currentIndex]];
        saveColumnSettings(newSettings);
    };

    // Reset to default columns
    const resetToDefaults = () => {
        saveColumnSettings(allColumns);
        toast.success('Column settings reset to default');
    };

    // Force reset column settings (for debugging)
    const forceResetColumns = () => {
        localStorage.removeItem('campaignColumnSettings');
        setColumnSettings([...allColumns]);
        toast.success('Forced column reset - page will reload');
        setTimeout(() => window.location.reload(), 1000);
    };

    // Modal handlers
    const openMessageModal = (message) => {
        setSelectedMessage(message);
        setShowMessageModal(true);
    };

    const closeMessageModal = () => {
        setShowMessageModal(false);
        setSelectedMessage('');
    };

    const openMediaModal = (mediaData, campaignType) => {
        setSelectedMedia({ ...mediaData, campaignType });
        setShowMediaModal(true);
    };

    const closeMediaModal = () => {
        setShowMediaModal(false);
        setSelectedMedia(null);
    };

    useEffect(() => {
        fetchCampaigns();
    }, [filters]);

    useEffect(() => {
        const handleWindowResize = () => {
            // Close dropdown on window resize to avoid positioning issues
            if (activeDropdown) {
                setActiveDropdown(null);
            }
        };

        const handleScroll = () => {
            // Close dropdown on scroll to avoid positioning issues
            if (activeDropdown) {
                setActiveDropdown(null);
            }
        };

        const handleEscapeKey = (event) => {
            if (event.key === 'Escape' && activeDropdown) {
                setActiveDropdown(null);
            }
        };

        window.addEventListener('resize', handleWindowResize);
        window.addEventListener('scroll', handleScroll, true);
        document.addEventListener('keydown', handleEscapeKey);

        return () => {
            window.removeEventListener('resize', handleWindowResize);
            window.removeEventListener('scroll', handleScroll, true);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [activeDropdown]);

    const fetchCampaigns = async () => {
        try {
            setLoading(true);
            // Use lightweight mode: reads embedded Campaign.statistics instead of
            // running expensive per-campaign CampaignContact aggregations (33M+ docs)
            const response = await campaignAPI.getCampaigns({ ...filters, lightweight: 'true' });
            setCampaigns(response.data.data.campaigns);
            setPagination(response.data.data.pagination);
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error('Campaigns fetch error:', error);
            }
            toast.error('Failed to fetch campaigns');
        } finally {
            setLoading(false);
            setPaginating(false); // Clear pagination loading when data is loaded
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1, // Reset to first page when filtering
        }));
    };

    const handlePageChange = (newPage) => {
        setPaginating(true); // Set pagination loading state
        setFilters(prev => ({
            ...prev,
            page: newPage,
        }));
    };

    const handleDeleteCampaign = (campaign) => {
        setCampaignToDelete(campaign);
        setDeleteConfirmText('');
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!campaignToDelete || deleteConfirmText !== 'Delete') return;

        try {
            setDeleting(campaignToDelete._id);
            await campaignAPI.deleteCampaign(campaignToDelete._id);
            toast.success('Campaign deleted successfully');
            fetchCampaigns(); // Refresh the list
            handleCancelDelete();
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to delete campaign';
            toast.error(errorMessage);
        } finally {
            setDeleting(null);
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
        setCampaignToDelete(null);
        setDeleteConfirmText('');
    };

    const downloadCSV = async (campaignId, filename) => {
        try {
            setDownloading(campaignId);
            setDownloadProgress(0);
            setDownloadSpeed(0);
            setDownloadTotalRecords(0);
            setDownloadProcessedRecords(0);
            setDownloadError(null);
            setDownloadStartTime(Date.now());
            setDownloadFileName(filename || `campaign_${campaignId}_detailed.csv`);
            setShowDownloadProgress(true);

            // Start the download process
            const startResponse = await campaignAPI.startCSVDownload(campaignId);

            if (!startResponse.data.success) {
                throw new Error(startResponse.data.message || 'Failed to start download');
            }

            const { progressId, totalRecords } = startResponse.data;
            setDownloadTotalRecords(totalRecords);

            // Poll for progress updates
            const pollProgress = async () => {
                try {
                    const progressResponse = await campaignAPI.getDownloadProgress(progressId);

                    if (progressResponse.data.success) {
                        const { status, progress, processedRecords, error, downloadUrl } = progressResponse.data;

                        setDownloadProgress(progress);
                        setDownloadProcessedRecords(processedRecords);

                        // Calculate download speed based on progress
                        const currentTime = Date.now();
                        const elapsed = downloadStartTime ? (currentTime - downloadStartTime) / 1000 : 0; // seconds
                        if (elapsed > 0 && processedRecords > 0) {
                            const recordsPerSecond = processedRecords / elapsed;
                            const estimatedBytesPerRecord = 80; // Average bytes per CSV row
                            const bytesPerSecond = Math.round(recordsPerSecond * estimatedBytesPerRecord);
                            setDownloadSpeed(bytesPerSecond);
                        } else {
                            setDownloadSpeed(0);
                        }

                        if (status === 'completed' && downloadUrl) {
                            // Download the generated file
                            const fileResponse = await campaignAPI.downloadGeneratedFile(progressId);

                            // Create blob and trigger download
                            const blob = new Blob([fileResponse.data], {
                                type: 'text/csv'
                            });

                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', filename || `campaign_${campaignId}_detailed.csv`);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            window.URL.revokeObjectURL(url);

                            setTimeout(() => {
                                toast.success('Campaign detailed file downloaded successfully');
                                setShowDownloadProgress(false);
                            }, 1500);

                            return; // Stop polling
                        } else if (status === 'error') {
                            throw new Error(error || 'Download failed');
                        } else if (status === 'processing' || status === 'preparing') {
                            // Continue polling
                            setTimeout(pollProgress, 1000); // Poll every second
                        }
                    }
                } catch (pollError) {
                    console.error('Progress poll error:', pollError);

                    // Handle specific error codes from server restart scenarios
                    const errorResponse = pollError.response?.data;
                    if (errorResponse?.code === 'EXPIRED') {
                        setDownloadError('Download session expired. Please start a new download.');
                        setTimeout(() => {
                            toast.error('Download session expired. Please start a new download.');
                            setShowDownloadProgress(false);
                        }, 2000);
                        return; // Stop polling
                    } else if (errorResponse?.code === 'NOT_FOUND') {
                        setDownloadError('Download session lost due to server restart. Please start a new download.');
                        setTimeout(() => {
                            toast.error('Download session lost due to server restart. Please start a new download.');
                            setShowDownloadProgress(false);
                        }, 2000);
                        return; // Stop polling
                    }

                    // For other errors, retry with longer delay
                    setTimeout(pollProgress, 2000);
                }
            };

            // Start polling
            setTimeout(pollProgress, 1000);

        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error('Download error:', error);
            }
            const errorMessage = error.response?.data?.message || error.message || 'Failed to download detailed file';
            setDownloadError(errorMessage);
            setTimeout(() => {
                toast.error(errorMessage);
                setShowDownloadProgress(false);
            }, 2000);
        } finally {
            setDownloading(null);
            setActiveDropdown(null);
        }
    };



    const downloadPhoneNumbers = async (campaignId, filename) => {
        try {
            setDownloadingPhoneNumbers(campaignId);
            setDownloadProgress(0);
            setDownloadSpeed(0);
            setDownloadTotalRecords(0);
            setDownloadProcessedRecords(0);
            setDownloadError(null);
            setDownloadStartTime(Date.now());
            setDownloadFileName(filename || `campaign_${campaignId}_phone_numbers.csv`);
            setShowDownloadProgress(true);

            // Start the phone numbers download process
            const startResponse = await campaignAPI.startPhoneNumbersDownload(campaignId);

            if (!startResponse.data.success) {
                throw new Error(startResponse.data.message || 'Failed to start download');
            }

            const { progressId, totalRecords } = startResponse.data;
            setDownloadTotalRecords(totalRecords);

            // Poll for progress updates
            const pollProgress = async () => {
                try {
                    const progressResponse = await campaignAPI.getDownloadProgress(progressId);

                    if (progressResponse.data.success) {
                        const { status, progress, processedRecords, error, downloadUrl } = progressResponse.data;

                        setDownloadProgress(progress);
                        setDownloadProcessedRecords(processedRecords);

                        // Calculate download speed based on progress
                        const currentTime = Date.now();
                        const elapsed = downloadStartTime ? (currentTime - downloadStartTime) / 1000 : 0; // seconds
                        if (elapsed > 0 && processedRecords > 0) {
                            const recordsPerSecond = processedRecords / elapsed;
                            const estimatedBytesPerRecord = 18; // Average bytes per phone number row
                            const bytesPerSecond = Math.round(recordsPerSecond * estimatedBytesPerRecord);
                            setDownloadSpeed(bytesPerSecond);
                        } else {
                            setDownloadSpeed(0);
                        }

                        if (status === 'completed' && downloadUrl) {
                            // Download the generated file
                            const fileResponse = await campaignAPI.downloadGeneratedFile(progressId);

                            // Create blob and trigger download
                            const blob = new Blob([fileResponse.data], {
                                type: 'text/csv'
                            });

                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', filename || `campaign_${campaignId}_phone_numbers.csv`);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            window.URL.revokeObjectURL(url);

                            setTimeout(() => {
                                toast.success('Phone numbers downloaded successfully');
                                setShowDownloadProgress(false);
                            }, 1500);

                            return; // Stop polling
                        } else if (status === 'error') {
                            throw new Error(error || 'Download failed');
                        } else if (status === 'processing' || status === 'preparing') {
                            // Continue polling
                            setTimeout(pollProgress, 1000); // Poll every second
                        }
                    }
                } catch (pollError) {
                    console.error('Progress poll error:', pollError);

                    // Handle specific error codes from server restart scenarios
                    const errorResponse = pollError.response?.data;
                    if (errorResponse?.code === 'EXPIRED') {
                        setDownloadError('Download session expired. Please start a new download.');
                        setTimeout(() => {
                            toast.error('Download session expired. Please start a new download.');
                            setShowDownloadProgress(false);
                        }, 2000);
                        return; // Stop polling
                    } else if (errorResponse?.code === 'NOT_FOUND') {
                        setDownloadError('Download session lost due to server restart. Please start a new download.');
                        setTimeout(() => {
                            toast.error('Download session lost due to server restart. Please start a new download.');
                            setShowDownloadProgress(false);
                        }, 2000);
                        return; // Stop polling
                    }

                    // For other errors, retry with longer delay
                    setTimeout(pollProgress, 2000);
                }
            };

            // Start polling
            setTimeout(pollProgress, 1000);

        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error('Download phone numbers error:', error);
            }
            const errorMessage = error.response?.data?.message || error.message || 'Failed to download phone numbers';
            setDownloadError(errorMessage);
            setTimeout(() => {
                toast.error(errorMessage);
                setShowDownloadProgress(false);
            }, 2000);
        } finally {
            setDownloadingPhoneNumbers(null);
            setActiveDropdown(null);
        }
    };

    const toggleDropdown = (campaignId, event) => {
        if (activeDropdown?.id === campaignId) {
            setActiveDropdown(null);
            return;
        }

        // Get the button's position relative to viewport
        const button = event.currentTarget;
        const rect = button.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;

        // Calculate available space
        const spaceBelow = windowHeight - rect.bottom;
        const spaceAbove = rect.top;
        const spaceRight = windowWidth - rect.right;
        const spaceLeft = rect.left;

        // Dropdown dimensions
        const dropdownHeight = isAdmin() ? 300 : 180;
        const dropdownWidth = 240;

        // Determine optimal positioning
        const shouldOpenUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
        const shouldAlignLeft = spaceRight < dropdownWidth && spaceLeft > spaceRight;

        // Calculate position with proper margins
        let top, left;

        if (shouldOpenUpward) {
            top = Math.max(10, rect.top - dropdownHeight - 8);
        } else {
            top = Math.min(windowHeight - dropdownHeight - 10, rect.bottom + 8);
        }

        if (shouldAlignLeft) {
            left = Math.max(10, rect.left);
        } else {
            left = Math.min(windowWidth - dropdownWidth - 10, rect.right - dropdownWidth);
        }

        setActiveDropdown({
            id: campaignId,
            position: { top, left },
            campaign: campaigns.find(c => c._id === campaignId)
        });
    };

    // Dropdown Overlay Component
    const DropdownOverlay = ({ dropdown, onClose }) => {
        if (!dropdown) return null;

        const { campaign, position } = dropdown;

        return createPortal(
            <div
                className="fixed inset-0 z-[9999]"
                onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        onClose();
                    }
                }}
            >
                <div
                    className="absolute bg-white rounded-lg shadow-2xl border border-gray-200 min-w-[15rem] max-w-[20rem]"
                    style={{
                        top: position.top,
                        left: position.left,
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="py-1" role="menu" aria-orientation="vertical">
                        {/* Download Options */}
                        <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-100">
                            <div className="flex items-center space-x-2">
                                <ArrowDownTrayIcon className="h-3 w-3" />
                                <span>Download Options</span>
                            </div>
                        </div>

                        {/* Download Phone Numbers */}
                        <button
                            onClick={() => {
                                downloadPhoneNumbers(campaign._id, `campaign_${campaign._id}_phone_numbers.csv`);
                                onClose();
                            }}
                            disabled={downloadingPhoneNumbers === campaign._id}
                            className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3 disabled:opacity-50 transition-colors"
                        >
                            {downloadingPhoneNumbers === campaign._id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-600 border-t-transparent"></div>
                            ) : (
                                <PhoneIcon className="h-4 w-4 text-blue-500" />
                            )}
                            <span>Download Phone</span>
                        </button>

                        {/* Download Campaign Detailed File */}
                        {(isAdmin() || (!isAdmin() && (campaign.status === 'Completed' || campaign.status === 'completed'))) && (
                            <button
                                onClick={() => {
                                    downloadCSV(campaign._id, `campaign_${campaign._id}_detailed.csv`);
                                    onClose();
                                }}
                                disabled={downloading === campaign._id}
                                className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3 disabled:opacity-50 transition-colors"
                            >
                                {downloading === campaign._id ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-600 border-t-transparent"></div>
                                ) : (
                                    <ArrowDownTrayIcon className="h-4 w-4 text-green-500" />
                                )}
                                <span>Campaign Detailed</span>
                            </button>
                        )}

                        {/* Admin Actions */}
                        {isAdmin() && (
                            <>
                                <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-100 border-t border-gray-100">
                                    <div className="flex items-center space-x-2">
                                        <Cog6ToothIcon className="h-3 w-3" />
                                        <span>Admin Actions</span>
                                    </div>
                                </div>

                                <Link
                                    to={`/campaigns/${campaign._id}/edit`}
                                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3 transition-colors"
                                    onClick={onClose}
                                >
                                    <PencilSquareIcon className="h-4 w-4 text-amber-500" />
                                    <span>Edit Campaign</span>
                                </Link>

                                <button
                                    onClick={() => {
                                        handleDeleteCampaign(campaign);
                                        onClose();
                                    }}
                                    disabled={deleting === campaign._id}
                                    className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-3 disabled:opacity-50 transition-colors rounded-b-lg"
                                >
                                    {deleting === campaign._id ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-600 border-t-transparent"></div>
                                    ) : (
                                        <TrashIcon className="h-4 w-4 text-red-500" />
                                    )}
                                    <span>Delete Campaign</span>
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>,
            document.body
        );
    };

    const campaignTypeIcons = {
        'Voice Call': SpeakerWaveIcon,
        'SMS': PhoneIcon,
        'Whatsapp': ChatBubbleLeftRightIcon,
        // Legacy support
        'voice': SpeakerWaveIcon,
        'sms': PhoneIcon,
        'whatsapp': ChatBubbleLeftRightIcon,
    };

    const getStatusBadge = (status) => {
        const statusClasses = {
            'Ongoing': 'bg-blue-100 text-blue-800',
            'Paused': 'bg-yellow-100 text-yellow-800',
            'Completed': 'bg-green-100 text-green-800',
            // Legacy support
            'pending': 'bg-yellow-100 text-yellow-800',
            'processing': 'bg-blue-100 text-blue-800',
            'completed': 'bg-green-100 text-green-800',
            'failed': 'bg-red-100 text-red-800'
        };

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
                {status}
            </span>
        );
    };

    if (loading && campaigns.length === 0) {
        return (
            <div className="space-y-6">
                {/* Header skeleton */}
                <div className="flex items-center justify-between">
                    <div>
                        <div className="h-8 bg-soft-200 rounded-lg w-48 animate-pulse"></div>
                        <div className="h-4 bg-soft-100 rounded-lg w-64 mt-2 animate-pulse"></div>
                    </div>
                    <div className="flex items-center space-x-3">
                        <div className="h-10 bg-soft-200 rounded-lg w-24 animate-pulse"></div>
                        <div className="h-10 bg-soft-200 rounded-lg w-32 animate-pulse"></div>
                    </div>
                </div>

                {/* Filters skeleton */}
                <div className="card">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="h-10 bg-soft-100 rounded-lg animate-pulse"></div>
                        <div className="h-10 bg-soft-100 rounded-lg animate-pulse"></div>
                        <div className="h-10 bg-soft-100 rounded-lg animate-pulse"></div>
                        <div className="h-10 bg-soft-100 rounded-lg animate-pulse"></div>
                    </div>
                </div>

                {/* Campaign loading skeleton */}
                <div className="card">
                    <div className="space-y-6">
                        {/* Loading header with animation */}
                        <div className="text-center py-8">
                            <div className="relative inline-flex items-center justify-center w-16 h-16 mb-4">
                                <div className="absolute inset-0 rounded-full border-4 border-primary-200 animate-spin border-t-primary-600"></div>
                                <div className="absolute inset-2 rounded-full border-2 border-primary-100 animate-spin border-t-primary-400" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
                                <DocumentTextIcon className="h-6 w-6 text-primary-600 animate-pulse" />
                            </div>
                            <h3 className="text-lg font-semibold text-soft-900 mb-2">Loading Your Campaigns</h3>
                            <p className="text-soft-600 mb-6">Fetching your latest campaign data...</p>

                            {/* Progress dots */}
                            <div className="flex items-center justify-center space-x-2">
                                <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce"></div>
                                <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            </div>
                        </div>

                        {/* Table skeleton */}
                        <div className="overflow-hidden">
                            {/* Table header skeleton */}
                            <div className="bg-soft-50 px-6 py-3 border-b border-soft-200">
                                <div className="grid grid-cols-6 gap-4">
                                    <div className="h-4 bg-soft-200 rounded animate-pulse"></div>
                                    <div className="h-4 bg-soft-200 rounded animate-pulse"></div>
                                    <div className="h-4 bg-soft-200 rounded animate-pulse"></div>
                                    <div className="h-4 bg-soft-200 rounded animate-pulse"></div>
                                    <div className="h-4 bg-soft-200 rounded animate-pulse"></div>
                                    <div className="h-4 bg-soft-200 rounded w-20 ml-auto animate-pulse"></div>
                                </div>
                            </div>

                            {/* Campaign row skeletons */}
                            {[...Array(5)].map((_, index) => (
                                <div key={index} className="px-6 py-4 border-b border-soft-100">
                                    <div className="grid grid-cols-6 gap-4 items-center">
                                        {/* Campaign type */}
                                        <div className="flex items-center">
                                            <div className="h-8 w-8 bg-soft-200 rounded-lg animate-pulse mr-3"></div>
                                            <div className="h-4 bg-soft-200 rounded w-20 animate-pulse"></div>
                                        </div>

                                        {/* Campaign name */}
                                        <div className="h-4 bg-soft-200 rounded w-32 animate-pulse"></div>

                                        {/* Message preview */}
                                        <div className="space-y-1">
                                            <div className="h-3 bg-soft-200 rounded w-full animate-pulse"></div>
                                            <div className="h-3 bg-soft-100 rounded w-3/4 animate-pulse"></div>
                                        </div>

                                        {/* Date */}
                                        <div className="h-4 bg-soft-200 rounded w-24 animate-pulse"></div>

                                        {/* Stats */}
                                        <div className="space-y-1">
                                            <div className="h-3 bg-soft-200 rounded w-16 animate-pulse"></div>
                                            <div className="h-3 bg-soft-100 rounded w-12 animate-pulse"></div>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex justify-end space-x-2">
                                            <div className="h-8 w-8 bg-soft-200 rounded animate-pulse"></div>
                                            <div className="h-8 w-8 bg-soft-200 rounded animate-pulse"></div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination skeleton */}
                        <div className="flex items-center justify-between pt-4">
                            <div className="h-4 bg-soft-200 rounded w-32 animate-pulse"></div>
                            <div className="flex space-x-2">
                                <div className="h-8 w-8 bg-soft-200 rounded animate-pulse"></div>
                                <div className="h-8 w-8 bg-soft-200 rounded animate-pulse"></div>
                                <div className="h-8 w-8 bg-soft-200 rounded animate-pulse"></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Fun loading messages that rotate */}
                <div className="text-center">
                    <div className="inline-flex items-center space-x-2 text-soft-500 text-sm">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-soft-300 border-t-primary-500"></div>
                        <span className="animate-pulse">Organizing your communication strategy...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Dropdown Overlay */}
            <DropdownOverlay
                dropdown={activeDropdown}
                onClose={() => setActiveDropdown(null)}
            />

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-soft-900">Campaigns</h1>
                    <p className="text-soft-600 mt-1">
                        {isAdmin()
                            ? 'Manage all communication campaigns'
                            : 'View and track your campaigns'}
                    </p>
                </div>
                <div className="flex items-center space-x-3">
                    {/* Column Settings Button */}
                    <button
                        onClick={() => setShowSettings(true)}
                        className="btn-secondary flex items-center space-x-2"
                        title="Customize Columns"
                    >
                        <Cog6ToothIcon className="h-5 w-5" />
                        <span>Columns</span>
                    </button>

                    <Link
                        to={isAdmin() ? "/campaigns/new" : "/campaigns/create"}
                        className="btn-primary flex items-center space-x-2"
                    >
                        <PlusIcon className="h-5 w-5" />
                        <span>New Campaign</span>
                    </Link>
                </div>
            </div>

            {/* Filters */}
            <div className="card">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-soft-400" />
                        <input
                            type="text"
                            placeholder="Search campaigns..."
                            className="input-field pl-10"
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                        />
                    </div>

                    <select
                        className="input-field"
                        value={filters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                        <option value="">All Status</option>
                        <option value="Ongoing">Ongoing</option>
                        <option value="Paused">Paused</option>
                        <option value="Completed">Completed</option>
                    </select>

                    <select
                        className="input-field"
                        value={filters.type}
                        onChange={(e) => handleFilterChange('type', e.target.value)}
                    >
                        <option value="">All Types</option>
                        <option value="Voice Call">Voice Call</option>
                        <option value="SMS">SMS</option>
                        <option value="Whatsapp">WhatsApp</option>
                    </select>

                    <select
                        className="input-field"
                        value={filters.limit}
                        onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                    >
                        <option value={10}>10 per page</option>
                        <option value={25}>25 per page</option>
                        <option value={50}>50 per page</option>
                    </select>
                </div>
            </div>

            {/* Campaigns List */}
            <div className="card">
                {campaigns.length > 0 ? (
                    <div className="overflow-x-auto relative">
                        <table className="min-w-full divide-y divide-soft-200">
                            <thead className="bg-soft-50">
                                <tr>
                                    {getVisibleColumns().map((column) => (
                                        <th key={column.key} className="px-6 py-3 text-left text-xs font-medium text-soft-500 uppercase tracking-wider">
                                            {column.label}
                                        </th>
                                    ))}
                                    <th className="px-6 py-3 text-right text-xs font-medium text-soft-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-soft-200">
                                {campaigns.map((campaign) => {
                                    const Icon = campaignTypeIcons[campaign.type] || DocumentTextIcon;
                                    const totalNumbers = campaign.statistics?.totalNumbers || campaign.csvFile?.totalNumbers || 0;
                                    const chargeCredit = campaign.chargePerNumber || 0;
                                    const totalCreditDeducted = totalNumbers * chargeCredit;
                                    const sentCount = campaign.statistics?.sent || 0;
                                    const deliveredCount = campaign.statistics?.delivered || 0;
                                    const failedCount = campaign.statistics?.failed || 0;

                                    return (
                                        <tr key={campaign._id} className="hover:bg-soft-50">
                                            {getVisibleColumns().map((column) => (
                                                <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                                                    {column.key === 'userName' && campaign.createdBy?.name}
                                                    {column.key === 'campaignType' && (
                                                        <div className="flex items-center">
                                                            <div className="h-8 w-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                                                                <Icon className="h-4 w-4 text-primary-600" />
                                                            </div>
                                                            <span className="text-sm font-medium text-soft-900">
                                                                {campaign.type}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {column.key === 'campaignName' && (
                                                        <div className="text-sm font-medium text-soft-900">
                                                            {campaign.title}
                                                        </div>
                                                    )}
                                                    {column.key === 'message' && (
                                                        <div className="max-w-[200px]">
                                                            {campaign.message ? (
                                                                <button
                                                                    onClick={() => openMessageModal(campaign.message)}
                                                                    className="block w-full text-sm text-soft-700 hover:text-primary-600 cursor-pointer text-left underline decoration-dotted hover:decoration-solid transition-all overflow-hidden"
                                                                    title="Click to view full message"
                                                                    style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                                                                >
                                                                    {campaign.message}
                                                                </button>
                                                            ) : (
                                                                <span className="text-sm text-soft-400 italic">No message</span>
                                                            )}
                                                        </div>
                                                    )}
                                                    {column.key === 'mediaFiles' && (
                                                        <div className="flex items-center space-x-2">
                                                            {/* Audio file for Voice Call campaigns */}
                                                            {campaign.type === 'Voice Call' && campaign.audioFile && (
                                                                <button
                                                                    onClick={() => openMediaModal(campaign.audioFile, 'Voice Call')}
                                                                    className="flex items-center space-x-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs hover:bg-blue-200 cursor-pointer transition-colors"
                                                                    title="Click to play audio"
                                                                >
                                                                    <MusicalNoteIcon className="h-3 w-3" />
                                                                    <span>Audio</span>
                                                                </button>
                                                            )}

                                                            {/* Media file for WhatsApp campaigns */}
                                                            {campaign.type === 'Whatsapp' && campaign.mediaFile && (
                                                                <button
                                                                    onClick={() => openMediaModal(campaign.mediaFile, 'Whatsapp')}
                                                                    className="flex items-center space-x-1 bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs hover:bg-green-200 cursor-pointer transition-colors"
                                                                    title="Click to view media"
                                                                >
                                                                    {campaign.mediaFile.mimetype?.startsWith('image/') ? (
                                                                        <>
                                                                            <PhotoIcon className="h-3 w-3" />
                                                                            <span>Image</span>
                                                                        </>
                                                                    ) : campaign.mediaFile.mimetype?.startsWith('video/') ? (
                                                                        <>
                                                                            <VideoCameraIcon className="h-3 w-3" />
                                                                            <span>Video</span>
                                                                        </>
                                                                    ) : campaign.mediaFile.mimetype === 'application/pdf' ? (
                                                                        <>
                                                                            <DocumentIcon className="h-3 w-3" />
                                                                            <span>PDF</span>
                                                                        </>
                                                                    ) : campaign.mediaFile.mimetype?.startsWith('audio/') ? (
                                                                        <>
                                                                            <MusicalNoteIcon className="h-3 w-3" />
                                                                            <span>Audio</span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <DocumentIcon className="h-3 w-3" />
                                                                            <span>File</span>
                                                                        </>
                                                                    )}
                                                                </button>
                                                            )}

                                                            {/* Show "None" if no media files */}
                                                            {!((campaign.type === 'Voice Call' && campaign.audioFile) ||
                                                                (campaign.type === 'Whatsapp' && campaign.mediaFile)) && (
                                                                    <span className="text-sm text-soft-400 italic">None</span>
                                                                )}
                                                        </div>
                                                    )}
                                                    {column.key === 'dateCreated' && (
                                                        <div className="text-sm text-soft-500">
                                                            {new Date(campaign.createdAt).toLocaleDateString()}
                                                        </div>
                                                    )}
                                                    {column.key === 'totalNumbers' && (
                                                        <div className="text-sm text-soft-900">
                                                            {totalNumbers.toLocaleString()}
                                                        </div>
                                                    )}
                                                    {column.key === 'chargeCredit' && (
                                                        <div className="text-sm text-soft-900">
                                                            {chargeCredit}
                                                        </div>
                                                    )}
                                                    {column.key === 'totalCreditDeducted' && (
                                                        <div className="text-sm text-soft-900">
                                                            {totalCreditDeducted.toLocaleString()}
                                                        </div>
                                                    )}
                                                    {column.key === 'sent' && (
                                                        <div className="text-sm text-soft-900">
                                                            {sentCount.toLocaleString()}
                                                        </div>
                                                    )}
                                                    {column.key === 'delivered' && (
                                                        <div className="text-sm text-soft-900">
                                                            {deliveredCount.toLocaleString()}
                                                        </div>
                                                    )}
                                                    {column.key === 'failed' && (
                                                        <div className="text-sm text-soft-900">
                                                            {failedCount.toLocaleString()}
                                                        </div>
                                                    )}
                                                    {column.key === 'status' && (
                                                        <div className="text-sm">
                                                            {getStatusBadge(campaign.status)}
                                                        </div>
                                                    )}
                                                </td>
                                            ))}
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex items-center justify-end space-x-2">
                                                    <Link
                                                        to={`/campaigns/${campaign._id}`}
                                                        className="text-primary-600 hover:text-primary-900"
                                                        title="View Details"
                                                    >
                                                        <EyeIcon className="h-5 w-5" />
                                                    </Link>

                                                    <button
                                                        onClick={(e) => toggleDropdown(campaign._id, e)}
                                                        className={`p-2 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1
                                                            ${activeDropdown?.id === campaign._id
                                                                ? 'text-primary-600 bg-primary-50'
                                                                : 'text-soft-400 hover:text-soft-600 hover:bg-soft-50'
                                                            }`}
                                                        title="More actions"
                                                        aria-haspopup="true"
                                                        aria-expanded={activeDropdown?.id === campaign._id}
                                                    >
                                                        <EllipsisVerticalIcon className="h-5 w-5" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <DocumentTextIcon className="mx-auto h-12 w-12 text-soft-400" />
                        <h3 className="mt-2 text-sm font-medium text-soft-900">No campaigns</h3>
                        <p className="mt-1 text-sm text-soft-500">
                            {filters.search || filters.status || filters.type
                                ? 'No campaigns match your current filters.'
                                : 'Get started by creating a new campaign.'}
                        </p>
                        {!filters.search && !filters.status && !filters.type && (
                            <div className="mt-6">
                                <Link
                                    to={isAdmin() ? "/campaigns/new" : "/campaigns/create"}
                                    className="btn-primary inline-flex items-center space-x-2"
                                >
                                    <PlusIcon className="h-5 w-5" />
                                    <span>New Campaign</span>
                                </Link>
                            </div>
                        )}
                    </div>
                )}

                {/* Pagination */}
                {pagination.pages > 1 && (
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-soft-200 sm:px-6">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => handlePageChange(pagination.current - 1)}
                                disabled={pagination.current === 1 || paginating}
                                className="relative inline-flex items-center px-4 py-2 border border-soft-300 text-sm font-medium rounded-md text-soft-700 bg-white hover:bg-soft-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {paginating ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent mr-2"></div>
                                ) : null}
                                Previous
                            </button>
                            <button
                                onClick={() => handlePageChange(pagination.current + 1)}
                                disabled={pagination.current === pagination.pages || paginating}
                                className="ml-3 relative inline-flex items-center px-4 py-2 border border-soft-300 text-sm font-medium rounded-md text-soft-700 bg-white hover:bg-soft-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {paginating ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent mr-2"></div>
                                ) : null}
                                Next
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-soft-700">
                                    Showing page <span className="font-medium">{pagination.current}</span> of{' '}
                                    <span className="font-medium">{pagination.pages}</span>
                                    {' '}({pagination.total} total campaigns)
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                    <button
                                        onClick={() => handlePageChange(pagination.current - 1)}
                                        disabled={pagination.current === 1 || paginating}
                                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-soft-300 bg-white text-sm font-medium text-soft-500 hover:bg-soft-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {paginating ? (
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent mr-2"></div>
                                        ) : null}
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => handlePageChange(pagination.current + 1)}
                                        disabled={pagination.current === pagination.pages || paginating}
                                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-soft-300 bg-white text-sm font-medium text-soft-500 hover:bg-soft-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {paginating ? (
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent mr-2"></div>
                                        ) : null}
                                        Next
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Column Settings Side Panel */}
            {showSettings && (
                <div className="modal-overlay fixed inset-0 z-50 overflow-hidden">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
                        onClick={() => setShowSettings(false)}
                    />

                    {/* Side Panel */}
                    <div className="modal-content absolute right-0 top-0 h-full w-96 bg-white shadow-xl transform transition-transform">
                        <div className="flex flex-col h-full">
                            {/* Header */}
                            <div className="flex items-center justify-between p-6 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900">Column Settings</h2>
                                <button
                                    onClick={() => setShowSettings(false)}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                                </button>
                            </div>

                            {/* Content */}
                            <div className="flex-1 overflow-y-auto p-6">
                                <div className="space-y-6">
                                    {/* Instructions */}
                                    <div className="bg-blue-50 p-4 rounded-lg">
                                        <h3 className="text-sm font-medium text-blue-800 mb-2">Customize Your View</h3>
                                        <p className="text-sm text-blue-700">
                                            Toggle column visibility and reorder them to match your workflow.
                                            Changes are saved automatically.
                                        </p>
                                    </div>

                                    {/* Column List */}
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-900 mb-3">Available Columns</h3>
                                        <div className="space-y-2">
                                            {columnSettings.map((column, index) => {
                                                // Skip admin-only columns for non-admin users
                                                if (column.admin && !isAdmin()) return null;

                                                return (
                                                    <div
                                                        key={column.key}
                                                        className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                                    >
                                                        <div className="flex items-center space-x-3">
                                                            <button
                                                                onClick={() => toggleColumnVisibility(column.key)}
                                                                className={`p-1 rounded transition-colors ${column.visible
                                                                    ? 'text-green-600 hover:bg-green-100'
                                                                    : 'text-gray-400 hover:bg-gray-100'
                                                                    }`}
                                                            >
                                                                {column.visible ? (
                                                                    <EyeIcon className="h-5 w-5" />
                                                                ) : (
                                                                    <EyeSlashIcon className="h-5 w-5" />
                                                                )}
                                                            </button>
                                                            <span className={`text-sm font-medium ${column.visible ? 'text-gray-900' : 'text-gray-500'
                                                                }`}>
                                                                {column.label}
                                                            </span>
                                                        </div>

                                                        <div className="flex items-center space-x-1">
                                                            <button
                                                                onClick={() => moveColumn(column.key, 'up')}
                                                                disabled={index === 0}
                                                                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                            >
                                                                <ChevronUpIcon className="h-4 w-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => moveColumn(column.key, 'down')}
                                                                disabled={index === columnSettings.length - 1}
                                                                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                            >
                                                                <ChevronDownIcon className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Quick Presets */}
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-900 mb-3">Quick Presets</h3>
                                        <div className="grid grid-cols-2 gap-2">
                                            <button
                                                onClick={() => {
                                                    const essentialColumns = ['campaignType', 'campaignName', 'dateCreated', 'totalNumbers', 'status'];
                                                    const newSettings = columnSettings.map(col => ({
                                                        ...col,
                                                        visible: essentialColumns.includes(col.key) || (col.admin && col.key === 'userName' && isAdmin())
                                                    }));
                                                    saveColumnSettings(newSettings);
                                                }}
                                                className="px-3 py-2 text-xs bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                                            >
                                                Essential View
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const performanceColumns = ['campaignName', 'totalNumbers', 'sent', 'delivered', 'failed', 'status'];
                                                    const newSettings = columnSettings.map(col => ({
                                                        ...col,
                                                        visible: performanceColumns.includes(col.key) || (col.admin && col.key === 'userName' && isAdmin())
                                                    }));
                                                    saveColumnSettings(newSettings);
                                                }}
                                                className="px-3 py-2 text-xs bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                                            >
                                                Performance View
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const financialColumns = ['campaignName', 'totalNumbers', 'chargeCredit', 'totalCreditDeducted', 'status'];
                                                    const newSettings = columnSettings.map(col => ({
                                                        ...col,
                                                        visible: financialColumns.includes(col.key) || (col.admin && col.key === 'userName' && isAdmin())
                                                    }));
                                                    saveColumnSettings(newSettings);
                                                }}
                                                className="px-3 py-2 text-xs bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100 transition-colors"
                                            >
                                                Financial View
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const newSettings = columnSettings.map(col => ({
                                                        ...col,
                                                        visible: !col.admin || isAdmin()
                                                    }));
                                                    saveColumnSettings(newSettings);
                                                }}
                                                className="px-3 py-2 text-xs bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
                                            >
                                                Show All
                                            </button>
                                        </div>
                                    </div>

                                    {/* Statistics */}
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <div className="grid grid-cols-2 gap-4 text-center">
                                            <div>
                                                <div className="text-2xl font-bold text-gray-900">
                                                    {getVisibleColumns().length}
                                                </div>
                                                <div className="text-sm text-gray-600">Visible Columns</div>
                                            </div>
                                            <div>
                                                <div className="text-2xl font-bold text-gray-900">
                                                    {columnSettings.filter(col => !col.admin || isAdmin()).length}
                                                </div>
                                                <div className="text-sm text-gray-600">Total Available</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="border-t border-gray-200 p-6">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                        <button
                                            onClick={resetToDefaults}
                                            className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                                        >
                                            Reset to Defaults
                                        </button>
                                        <button
                                            onClick={forceResetColumns}
                                            className="text-sm text-red-600 hover:text-red-900 transition-colors"
                                        >
                                            Force Reset (Debug)
                                        </button>
                                    </div>
                                    <button
                                        onClick={() => setShowSettings(false)}
                                        className="btn-primary"
                                    >
                                        Done
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && campaignToDelete && (
                <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="modal-content bg-white rounded-xl shadow-xl max-w-md w-full">
                        <div className="p-6">
                            <div className="flex items-center justify-center mb-4">
                                <div className="h-12 w-12 bg-error-100 rounded-full flex items-center justify-center">
                                    <TrashIcon className="h-6 w-6 text-error-600" />
                                </div>
                            </div>

                            <div className="text-center mb-6">
                                <h2 className="text-xl font-bold text-soft-900 mb-2">
                                    Delete Campaign
                                </h2>
                                <p className="text-soft-600">
                                    Are you sure you want to delete campaign{' '}
                                    <span className="font-medium text-soft-900">"{campaignToDelete.title}"</span>?
                                </p>
                                <p className="text-soft-500 text-sm mt-2">
                                    This action cannot be undone and will permanently remove the campaign and all associated data.
                                </p>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-soft-700 mb-2">
                                    To confirm deletion, type <span className="font-bold text-error-600">Delete</span> in the box below:
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={deleteConfirmText}
                                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                                        className={`input-field text-center font-medium pr-10 ${deleteConfirmText === 'Delete'
                                            ? 'border-success-500 focus:ring-success-500 focus:border-success-500'
                                            : deleteConfirmText.length > 0
                                                ? 'border-error-500 focus:ring-error-500 focus:border-error-500'
                                                : ''
                                            }`}
                                        placeholder="Type 'Delete' to confirm"
                                        disabled={deleting}
                                    />
                                    {deleteConfirmText === 'Delete' && (
                                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                            <CheckIcon className="h-5 w-5 text-success-500" />
                                        </div>
                                    )}
                                </div>
                                {deleteConfirmText.length > 0 && deleteConfirmText !== 'Delete' && (
                                    <p className="text-error-600 text-sm mt-1">
                                        Please type "Delete" exactly as shown
                                    </p>
                                )}
                            </div>

                            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                                <button
                                    type="button"
                                    onClick={handleCancelDelete}
                                    disabled={deleting}
                                    className="btn-secondary disabled:opacity-50 w-full sm:w-auto"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirmDelete}
                                    disabled={deleting || deleteConfirmText !== 'Delete'}
                                    className="btn-error flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                                >
                                    {deleting ? (
                                        <>
                                            <div className="loading-spinner"></div>
                                            <span>Deleting...</span>
                                        </>
                                    ) : (
                                        <>
                                            <TrashIcon className="h-5 w-5" />
                                            <span className="hidden sm:inline">Delete Campaign</span>
                                            <span className="sm:hidden">Delete</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Message Modal */}
            {showMessageModal && (
                <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="modal-content bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-xl font-bold text-soft-900">Campaign Message</h2>
                                <button
                                    onClick={closeMessageModal}
                                    className="text-soft-400 hover:text-soft-600 transition-colors"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>
                            <div className="max-h-96 overflow-y-auto">
                                <div className="bg-soft-50 rounded-lg p-4">
                                    <p className="text-soft-700 whitespace-pre-wrap leading-relaxed">
                                        {selectedMessage}
                                    </p>
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end">
                                <button
                                    onClick={closeMessageModal}
                                    className="btn-primary"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Media Modal */}
            {showMediaModal && selectedMedia && (
                <div className="modal-overlay fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="modal-content bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-xl font-bold text-soft-900">
                                    {selectedMedia.campaignType === 'Voice Call' ? 'Audio File' : 'Media File'}
                                </h2>
                                <button
                                    onClick={closeMediaModal}
                                    className="text-soft-400 hover:text-soft-600 transition-colors"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>

                            <div className="flex flex-col items-center justify-center min-h-[300px]">
                                {/* Audio Player for Voice Call campaigns */}
                                {selectedMedia.campaignType === 'Voice Call' && (
                                    <div className="w-full max-w-md">
                                        <div className="bg-blue-50 rounded-lg p-6 text-center mb-4">
                                            <MusicalNoteIcon className="h-16 w-16 text-blue-500 mx-auto mb-4" />
                                            <p className="text-soft-700 mb-2">Audio File</p>
                                            <p className="text-sm text-soft-500 mb-4">
                                                {selectedMedia.originalName || 'Audio file'}
                                            </p>
                                        </div>
                                        <audio
                                            controls
                                            className="w-full"
                                            src={getMediaUrl(selectedMedia.filename)}
                                        >
                                            Your browser does not support the audio element.
                                        </audio>
                                    </div>
                                )}

                                {/* Image Display for WhatsApp campaigns */}
                                {selectedMedia.campaignType === 'Whatsapp' && selectedMedia.mimetype?.startsWith('image/') && (
                                    <div className="w-full max-w-3xl">
                                        <img
                                            src={getMediaUrl(selectedMedia.filename)}
                                            alt="Campaign Media"
                                            className="w-full h-auto max-h-[60vh] object-contain rounded-lg shadow-lg"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'block';
                                            }}
                                        />
                                        <div className="hidden bg-soft-50 rounded-lg p-8 text-center">
                                            <PhotoIcon className="h-16 w-16 text-soft-400 mx-auto mb-4" />
                                            <p className="text-soft-600">Unable to load image</p>
                                            <p className="text-sm text-soft-500 mt-2">
                                                {selectedMedia.originalName || 'Image file'}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Video Player for WhatsApp campaigns */}
                                {selectedMedia.campaignType === 'Whatsapp' && selectedMedia.mimetype?.startsWith('video/') && (
                                    <div className="w-full max-w-3xl">
                                        <video
                                            controls
                                            className="w-full h-auto max-h-[60vh] rounded-lg shadow-lg"
                                            src={getMediaUrl(selectedMedia.filename)}
                                        >
                                            Your browser does not support the video element.
                                        </video>
                                    </div>
                                )}

                                {/* PDF Preview for WhatsApp campaigns */}
                                {selectedMedia.campaignType === 'Whatsapp' && selectedMedia.mimetype === 'application/pdf' && (
                                    <div className="w-full max-w-5xl">
                                        <iframe
                                            title={selectedMedia.originalName || 'Campaign PDF'}
                                            src={getMediaUrl(selectedMedia.filename)}
                                            className="w-full h-[70vh] rounded-lg shadow-lg bg-white"
                                        />
                                    </div>
                                )}

                                {/* Audio Player for WhatsApp campaigns */}
                                {selectedMedia.campaignType === 'Whatsapp' && selectedMedia.mimetype?.startsWith('audio/') && (
                                    <div className="w-full max-w-3xl">
                                        <audio
                                            controls
                                            className="w-full"
                                            src={getMediaUrl(selectedMedia.filename)}
                                        >
                                            Your browser does not support the audio element.
                                        </audio>
                                    </div>
                                )}

                                {/* Other file types */}
                                {selectedMedia.campaignType === 'Whatsapp' &&
                                    !selectedMedia.mimetype?.startsWith('image/') &&
                                    !selectedMedia.mimetype?.startsWith('video/') &&
                                    selectedMedia.mimetype !== 'application/pdf' &&
                                    !selectedMedia.mimetype?.startsWith('audio/') && (
                                        <div className="w-full max-w-md">
                                            <div className="bg-soft-50 rounded-lg p-8 text-center">
                                                <DocumentIcon className="h-16 w-16 text-soft-400 mx-auto mb-4" />
                                                <p className="text-soft-700 mb-2">File</p>
                                                <p className="text-sm text-soft-500 mb-4">
                                                    {selectedMedia.originalName || 'Media file'}
                                                </p>
                                                <p className="text-xs text-soft-400">
                                                    Type: {selectedMedia.mimetype || 'Unknown'}
                                                </p>
                                                <a
                                                    href={getMediaUrl(selectedMedia.filename)}
                                                    download={selectedMedia.originalName}
                                                    className="btn-primary mt-4 inline-block"
                                                >
                                                    Download File
                                                </a>
                                            </div>
                                        </div>
                                    )}
                            </div>

                            <div className="mt-6 flex justify-between items-center">
                                <div className="text-sm text-soft-500">
                                    {selectedMedia.originalName && (
                                        <span>Filename: {selectedMedia.originalName}</span>
                                    )}
                                </div>
                                <button
                                    onClick={closeMediaModal}
                                    className="btn-primary"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Download Progress Modal */}
            <DownloadProgress
                progress={downloadProgress}
                fileName={downloadFileName}
                downloadSpeed={downloadSpeed}
                totalRecords={downloadTotalRecords}
                processedRecords={downloadProcessedRecords}
                startTime={downloadStartTime}
                isVisible={showDownloadProgress}
                error={downloadError}
                onCancel={() => {
                    setShowDownloadProgress(false);
                    setDownloadProgress(0);
                    setDownloadSpeed(0);
                    setDownloadTotalRecords(0);
                    setDownloadProcessedRecords(0);
                    setDownloadStartTime(null);
                    setDownloadError(null);
                    setDownloadFileName('');
                }}
            />
        </div>
    );
};

export default Campaigns; 